'use client';

import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { useTranslations } from 'next-intl';
import { Icon, type IconName } from '@/lib/ui-kit/icons';
import { cn } from '@/lib/ui-kit/utils';
import { LABEL_CLASS } from '../constants';
import type { PriceAlertCondition } from '../types';

interface ConditionOption {
  value: PriceAlertCondition;
  labelKey:
    | 'priceAbove'
    | 'priceBelow'
    | 'volumeAbove'
    | 'volumeBelow'
    | 'oiAbove'
    | 'oiBelow'
    | 'whaleTrade';
  icon: IconName;
}

const ROW_TOP: ConditionOption[] = [
  { value: 'PRICE_ABOVE', labelKey: 'priceAbove', icon: 'ArrowUp' },
  { value: 'PRICE_BELOW', labelKey: 'priceBelow', icon: 'ArrowDown' },
  { value: 'VOLUME_24H_ABOVE', labelKey: 'volumeAbove', icon: 'VolumeUp' },
  { value: 'VOLUME_24H_BELOW', labelKey: 'volumeBelow', icon: 'VolumeDown' },
];

const ROW_BOTTOM: ConditionOption[] = [
  { value: 'BOOK_DEPTH_ABOVE', labelKey: 'oiAbove', icon: 'OrderbookAll' },
  { value: 'BOOK_DEPTH_BELOW', labelKey: 'oiBelow', icon: 'OrderbookAll' },
  { value: 'WHALE_TRADE', labelKey: 'whaleTrade', icon: 'Whale' },
];

interface ConditionFieldProps {
  value: PriceAlertCondition;
  onChange: (value: PriceAlertCondition) => void;
}

function ConditionItem({ option, isActive }: { option: ConditionOption; isActive: boolean }) {
  const tf = useTranslations('priceAlerts.form');
  return (
    <RadioGroupPrimitive.Item
      value={option.value}
      className={cn(
        'flex h-10 flex-auto cursor-pointer items-center justify-center gap-1 rounded-[10px] py-2 pl-2 pr-3 outline-none transition-colors focus-visible:ring-1 focus-visible:ring-v2-border',
        isActive
          ? 'bg-v2-surface-primary text-v2-text-primary shadow-sm dark:bg-v2-border dark:shadow-none'
          : 'bg-v2-btn-secondary text-v2-text-secondary hover:text-v2-text-primary',
      )}
    >
      <Icon name={option.icon} size={20} className="shrink-0" />
      <span className="whitespace-nowrap text-[12px] leading-[115%] font-normal">
        {tf(`conditions.${option.labelKey}`)}
      </span>
    </RadioGroupPrimitive.Item>
  );
}

export function ConditionField({ value, onChange }: ConditionFieldProps) {
  const tf = useTranslations('priceAlerts.form');

  return (
    <div className="flex flex-col gap-2">
      <span className={LABEL_CLASS}>{tf('condition')}</span>
      <RadioGroupPrimitive.Root
        value={value}
        onValueChange={(v) => onChange(v as PriceAlertCondition)}
        className="flex flex-col gap-2"
        aria-label={tf('condition')}
      >
        <div className="grid w-full grid-cols-2 gap-2 min-[800px]:grid-cols-4">
          {ROW_TOP.map((option) => (
            <ConditionItem key={option.value} option={option} isActive={value === option.value} />
          ))}
        </div>
        <div className="grid w-full grid-cols-2 gap-2 min-[800px]:grid-cols-3 [&>:last-child]:col-span-2 min-[800px]:[&>:last-child]:col-span-1">
          {ROW_BOTTOM.map((option) => (
            <ConditionItem key={option.value} option={option} isActive={value === option.value} />
          ))}
        </div>
      </RadioGroupPrimitive.Root>
    </div>
  );
}
